import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBasket, Menu, X, Download } from "lucide-react";
import { CATALOGUE_URL } from "../seo";
import { useSite } from "./SiteContext";
import { useCatalogue, displayName } from "./useCatalogue";

export default function Nav() {
  const { count, setPanelOpen } = useSite();
  const { categories } = useCatalogue();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const closeTimer = useRef(null);

  const onDarkHero = loc.pathname === "/";
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobileOpen(false); setMegaOpen(false); }, [loc.pathname]);

  const openMega = () => { clearTimeout(closeTimer.current); setMegaOpen(true); };
  const closeMega = () => { closeTimer.current = setTimeout(() => setMegaOpen(false), 180); };

  return (
    <>
      <header className={`nav ${scrolled || !onDarkHero ? "solid" : ""}`} data-testid="main-nav">
        <Link to="/" className="nav-logo" data-testid="nav-logo">SEPLA</Link>
        <nav className="nav-links">
          <div className="nav-item" onMouseEnter={openMega} onMouseLeave={closeMega}>
            <NavLink to="/products" data-testid="nav-products">Products</NavLink>
            <div className={`mega ${megaOpen ? "open" : ""}`} onMouseEnter={openMega} onMouseLeave={closeMega} data-testid="products-mega-menu">
              {categories.map((c) => (
                <Link key={c.slug} to={`/category/${c.slug}`} className="mega-tile" data-testid={`mega-${c.slug}`}>
                  <img src={c.cover} alt={c.name} loading="lazy" />
                  <span className="mega-num">{c.num}</span>
                  <span className="mega-name">{displayName(c.name)}</span>
                  <span className="mega-sub">{c.count} products</span>
                </Link>
              ))}
            </div>
          </div>
          <NavLink to="/about" data-testid="nav-about">About</NavLink>
          <NavLink to="/custom-oem" data-testid="nav-oem">Custom &amp; OEM</NavLink>
          <NavLink to="/product-index" data-testid="nav-index">Product Index</NavLink>
          <NavLink to="/contact" data-testid="nav-contact">Contact</NavLink>
        </nav>
        <div className="nav-right">
          <button id="enquiry-nav-btn" className="enquiry-btn" onClick={() => setPanelOpen(true)} data-testid="enquiry-nav-button" aria-label="Open enquiry list">
            <ShoppingBasket size={17} />
            {count > 0 && <span className="enquiry-count" key={count} data-testid="enquiry-count">{count}</span>}
          </button>
          <a href={CATALOGUE_URL} download className="btn btn-ghost nav-catalogue" data-testid="nav-download-catalogue"><Download size={14} /> Catalogue</a>
          <Link to="/contact" className="btn btn-accent nav-quote" data-testid="nav-get-quote">Get a quote</Link>
          <button className="nav-burger" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="nav-burger"><Menu size={22} /></button>
        </div>
      </header>

      {/* mobile full-screen menu */}
      <div className={`mobilemenu ${mobileOpen ? "open" : ""}`} data-testid="mobile-menu">
        <button className="mobilemenu-close" onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close"><X size={26} /></button>
        <div className="mobilemenu-links">
          {categories.map((c, i) => (
            <Link key={c.slug} to={`/category/${c.slug}`} style={{ transitionDelay: `${i * 60 + 100}ms` }} className="mm-cat">
              <span className="mm-num">{c.num}</span> {displayName(c.name)}
            </Link>
          ))}
          <div className="mm-row">
            <Link to="/products">All products</Link>
            <Link to="/about">About</Link>
            <Link to="/custom-oem">Custom &amp; OEM</Link>
            <Link to="/product-index">Index</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div className="mm-ctas">
            <Link to="/contact" className="btn btn-accent">Get a quote</Link>
            <a href={CATALOGUE_URL} download className="btn btn-ghost" data-testid="mobile-download-catalogue"><Download size={14} /> Download catalogue</a>
          </div>
        </div>
      </div>
    </>
  );
}
